"use client";
// Fade-up-on-scroll wrapper. Sets `data-revealed="true"` once the element
// crosses 10% into the viewport; CSS in globals.css handles the transition.
//
// Use sparingly on large editorial moments (section headings, hero copy
// blocks, big imagery). Don't use on every card or list item — that's
// where the "AI slop" reveal-everything aesthetic comes from.

import { useEffect, useRef } from "react";

interface Props {
  children: React.ReactNode;
  delay?: number; // ms
  className?: string;
}

export function Reveal({ children, delay = 0, className }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Already in viewport on mount? Reveal immediately (no flash on refresh).
    const rect = el.getBoundingClientRect();
    const inViewport = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
    if (inViewport) {
      const t = setTimeout(() => el.setAttribute("data-revealed", "true"), delay);
      return () => clearTimeout(t);
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.setAttribute("data-revealed", "true"), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  return (
    <div ref={ref} data-reveal className={className}>
      {children}
    </div>
  );
}
